import { Prettify } from 'viem';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Gwei } from '../../../lib/gwei';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { alignToValidSize, divide, multiply } from '../../../lib/math';
import { OrderType, TimeInForce } from '../../../types/orders';

export type TradeFormState = {
  amount?: bigint;
  totalDisplay?: bigint;
  activeInput: 'amount' | 'total'; // Represents which input is currently active
  orderType: OrderType;
  side: 'long' | 'short';
  tif: TimeInForce;
  expiryInDays: number;
  reduceOnly: boolean;
  postOnly: boolean;
  showTpSl: boolean;
  stopLoss?: bigint;
  takeProfit?: bigint;
  limitPrice?: bigint;
  executionPriceLong?: bigint;
  executionPriceShort?: bigint;
  midPrice?: bigint;
  lotSize?: bigint;
  tickSize?: bigint;
  // Action methods
  setActiveInput: (activeInput: 'amount' | 'total') => void;
  setOrderType: (orderType: OrderType, updateQs: (orderType: OrderType) => void) => void;
  setSide: (side: 'long' | 'short', updateQs: (side: 'long' | 'short') => void) => void;
  setTif: (side: TimeInForce) => void;
  setExpiryInDays: (days: number) => void;
  setReduceOnly: (reduceOnly: boolean) => void;
  setPostOnly: (postOnly: boolean) => void;
  setShowTpSl: (showTpSl: boolean) => void;
  setAmount: (amount: bigint | undefined) => void;
  setStopLoss: (stopLoss: bigint | undefined) => void;
  setTakeProfit: (takeProfit: bigint | undefined) => void;
  setTotalDisplay: (totalDisplay: bigint | undefined) => void;
  setLimitPrice: (limitPrice: bigint | undefined) => void;
  setExecutionPriceLong: (executionPriceLong: bigint | undefined) => void;
  setExecutionPriceShort: (executionPriceShort: bigint | undefined) => void;
  setMidPrice: (midPrice: bigint | undefined) => void;
  setProduct: (
    lotSize: bigint,
    tickSize: bigint,
    updateQs: (lotSize: bigint, tickSize: bigint) => void
  ) => void;
};

type GetPriceToUseFromStateArg = Prettify<
  Pick<
    TradeFormState,
    'orderType' | 'limitPrice' | 'executionPriceLong' | 'executionPriceShort' | 'side' | 'midPrice'
  >
>;
// Helper function to get the price to use based on the state
export const getPriceToUseFromState = (state: GetPriceToUseFromStateArg) => {
  if (state.orderType === 'LIMIT') {
    return state.limitPrice;
  }
  const executionPrice =
    state.side === 'long' ? state.executionPriceLong : state.executionPriceShort;
  // If its a market order and there's not enough liquidity return the mid price.
  // The ui will be aware that liquidity is missing, but can use the mid price to populate slider and totalDisplay input etc.
  return executionPrice || state.midPrice;
};

export const useTradeFormStore = create<TradeFormState>()(
  persist(
    (set, get) => {
      // Utility function for amount and totalDisplay calculations
      const calcAmountAndTotal = (
        state: TradeFormState,
        priceToUse: bigint
      ): Pick<TradeFormState, 'amount' | 'totalDisplay'> =>
        state.activeInput === 'amount'
          ? {
              totalDisplay:
                state.amount !== undefined ? multiply(state.amount, priceToUse) : undefined,
            }
          : {
              amount:
                state.totalDisplay !== undefined
                  ? alignToValidSize(
                      divide(state.totalDisplay, priceToUse),
                      state.lotSize || Gwei.toGwei('0.01')
                    )
                  : undefined,
            };

      return {
        // Initial state
        amount: undefined,
        totalDisplay: undefined,
        activeInput: 'amount',
        orderType: 'MARKET',
        side: 'long',
        tif: 'GTD',
        expiryInDays: 30,
        reduceOnly: false,
        postOnly: false,
        showTpSl: false,
        stopLoss: undefined,
        takeProfit: undefined,
        limitPrice: undefined,
        executionPriceLong: undefined,
        executionPriceShort: undefined,
        midPrice: undefined,
        lotSize: undefined,
        tickSize: undefined,

        // Action methods
        setActiveInput: (activeInput) => {
          const state = get();
          const newState = { ...state, activeInput };
          const priceToUse = getPriceToUseFromState(newState);

          if (priceToUse) {
            // Note that we use the old state to calc the new amount and totalDisplay.
            // We do this cause, if amount is active, we want a fresh totalDisplay as its getting switched to.
            set({ activeInput, ...calcAmountAndTotal(state, priceToUse) });
          } else {
            set({ activeInput });
          }
        },
        setTif: (tif) => {
          set({ tif });
        },
        setExpiryInDays: (expiryInDays) => {
          set({ expiryInDays });
        },
        setReduceOnly: (reduceOnly) => {
          set({ reduceOnly });
        },
        setPostOnly: (postOnly) => {
          set({ postOnly });
        },
        setShowTpSl: (showTpSl) => {
          set({ showTpSl });
        },
        setStopLoss: (stopLoss) => {
          set({ stopLoss });
        },
        setTakeProfit: (takeProfit) => {
          set({ takeProfit });
        },
        setProduct: (lotSize, tickSize, updateQs) => {
          updateQs(lotSize, tickSize);
          set({ lotSize, tickSize });
        },

        setOrderType: (orderType, updateQs) => {
          const state = get();
          updateQs(orderType);

          const stateWithNewOrderType = { ...state, orderType };
          const priceToUse = getPriceToUseFromState(stateWithNewOrderType);

          // When toggling to limit order, set it to mid price if it's not set.
          const limitPriceRaw = state.limitPrice || state.midPrice;
          const tickSize = state.tickSize || Gwei.toGwei('0.01');
          const limitPrice = limitPriceRaw ? alignToValidSize(limitPriceRaw, tickSize) : undefined;

          if (!priceToUse) {
            const newTotalOrAmount =
              state.activeInput === 'amount' ? { totalDisplay: undefined } : { amount: undefined };
            set({ orderType, limitPrice, ...newTotalOrAmount });
            return;
          }

          const amountAndTotalState = calcAmountAndTotal(state, priceToUse);
          set({ ...amountAndTotalState, orderType, limitPrice });
        },

        setSide: (side, updateQs) => {
          const state = get();
          updateQs(side);

          if (!state.amount || !state.totalDisplay || state.orderType === 'LIMIT') {
            set({ side });
            return;
          }

          const stateWithNewSide = { ...state, side };
          const priceToUse = getPriceToUseFromState(stateWithNewSide);

          if (!priceToUse) {
            set({ side });
            return;
          }

          const amountAndTotalState = calcAmountAndTotal(stateWithNewSide, priceToUse);

          set({ ...amountAndTotalState, side });
        },

        setAmount: (amount) => {
          const state = get();
          if (amount === undefined) {
            set({
              totalDisplay: undefined,
              amount: undefined,
            });
            return;
          }
          const priceToUse = getPriceToUseFromState(state);
          const totalDisplay = priceToUse ? multiply(amount, priceToUse) : undefined;
          set({ amount, totalDisplay });
        },

        setTotalDisplay: (totalDisplay) => {
          const state = get();
          if (totalDisplay === undefined) {
            set({
              totalDisplay: undefined,
              amount: undefined,
            });
            return;
          }
          const priceToUse = getPriceToUseFromState(state);

          const rawAmount = priceToUse ? divide(totalDisplay, priceToUse) : undefined;
          const amount =
            rawAmount !== undefined && state.lotSize
              ? alignToValidSize(rawAmount, state.lotSize)
              : undefined;

          set({
            amount,
            totalDisplay,
          });
        },

        setLimitPrice: (rawLimitPrice) => {
          // Tick size should always be defined here, as the trade page wont render without a loaded product.
          const tickSize = get().tickSize || Gwei.toGwei('0.01');
          const limitPrice =
            rawLimitPrice === undefined ? undefined : alignToValidSize(rawLimitPrice, tickSize);
          const state = get();
          if (
            state.orderType === 'MARKET' ||
            (state.amount === undefined && state.totalDisplay === undefined)
          ) {
            set({ limitPrice });
            return;
          }

          if (limitPrice === undefined || limitPrice === 0n) {
            const amountAndTotalState =
              state.activeInput === 'amount'
                ? { totalDisplay: limitPrice }
                : { amount: limitPrice };
            set({ ...amountAndTotalState, limitPrice });
            return;
          }

          const amountAndTotalState = calcAmountAndTotal(state, limitPrice);
          set({ ...amountAndTotalState, limitPrice });
        },

        setExecutionPriceLong: (executionPriceLong) => {
          const state = get();
          const newState = { ...state, executionPriceLong };
          const prevPriceToUse = getPriceToUseFromState(state);
          const newPriceToUse = getPriceToUseFromState(newState);

          if (!prevPriceToUse && newPriceToUse) {
            const amountAndTotalState = calcAmountAndTotal(newState, newPriceToUse);

            set({ ...amountAndTotalState, executionPriceLong });
          } else {
            set({ executionPriceLong });
          }
        },
        setExecutionPriceShort: (executionPriceShort) => {
          const state = get();
          const newState = { ...state, executionPriceShort };

          const prevPriceToUse = getPriceToUseFromState(state);
          const newPriceToUse = getPriceToUseFromState(newState);

          if (!prevPriceToUse && newPriceToUse) {
            const amountAndTotalState = calcAmountAndTotal(newState, newPriceToUse);
            set({ ...amountAndTotalState, executionPriceShort });
          } else {
            set({ executionPriceShort });
          }
        },
        setMidPrice: (midPrice) => {
          const state = get();

          // This make sure we have limit price set if users visit the trade page with orderType=LIMIT set in the querystring
          const limitPriceRaw =
            state.orderType === 'LIMIT' ? state.limitPrice || midPrice : undefined;
          const tickSize = state.tickSize || Gwei.toGwei('0.01');
          const limitPrice = limitPriceRaw ? alignToValidSize(limitPriceRaw, tickSize) : undefined;

          set({ midPrice, limitPrice });
        },
      };
    },
    {
      name: localStorageKeys.tradeFormStore,
      partialize: (state) => ({ activeInput: state.activeInput }),
    }
  )
);
